import type { SixPillarPreviewId } from "@/lib/six-pillars-preview";

type MonthToken =
  | "01"
  | "02"
  | "03"
  | "04"
  | "05"
  | "06"
  | "07"
  | "08"
  | "09"
  | "10"
  | "11"
  | "12";

export type RoadmapMonth = `${number}-${MonthToken}`;

export type RoadmapPhase = "shipped" | "planned";

export type RoadmapLink = {
  href: string;
  label: string;
  external?: boolean;
};

export type RoadmapImage = {
  src: string;
  alt: string;
  caption?: string;
};

export type RoadmapMedia =
  | ({
      type: "image";
      /** Set for roadmap covers, which show at their own ratio instead of the 16:9 frame. */
      width?: number;
      height?: number;
    } & RoadmapImage)
  | {
      /** The homepage's interactive product previews, switched by tabs. */
      type: "previews";
      pillars: SixPillarPreviewId[];
    }
  | {
      type: "video";
      embedUrl: string;
      title: string;
      caption?: string;
    };

export type RoadmapEntry = {
  phase: RoadmapPhase;
  month: RoadmapMonth;
  order?: number;
  anchorId?: string;
  title: string;
  eyebrow: string;
  summary: string;
  details?: string[];
  media?: RoadmapMedia;
  links?: RoadmapLink[];
};

export const roadmapPageContent = {
  title: "Roadmap",
  lead:
    "A running view of what Cooperly has already shipped and what we are building next.",
} as const;

export const roadmapEntries: RoadmapEntry[] = [
  {
    phase: "shipped",
    month: "2026-02",
    title: "Onboarding + Mock Data",
    eyebrow: "Team setup",
    summary:
      "We launched a guided onboarding flow with mock data so new teams could start inside a realistic workspace instead of a blank product.",
    details: [
      "New workspaces now have a clearer first-run experience for pilots and guided rollouts.",
      "Mock data helps leaders understand the rhythm of the product before live team signals begin to appear.",
      "This made the early Cooperly experience easier to explain, demo, and adopt.",
    ],
    media: {
      type: "image",
      src: "/cooperly/roadmap-onboarding-mock-data-v3.png",
      alt: "Onboarding and mock data preview in Cooperly",
      caption: "Guided onboarding preview with a realistic starting workspace.",
    },
  },
  {
    phase: "shipped",
    month: "2026-03",
    title: "Candidates Model",
    eyebrow: "Signal model",
    summary:
      "We introduced the candidates model to make Cooperly sharper about who may need attention, what context matters, and where follow-up should happen first.",
    details: [
      "The model creates a cleaner layer for ranking and reasoning about people-related signals.",
      "It gives leaders more useful context before a situation turns into a missed conversation or a retention risk.",
    ],
    media: {
      type: "image",
      src: "/cooperly/roadmap-candidates-model-v3.png",
      alt: "Candidates model screen in Cooperly",
      caption: "Candidates model interface.",
    },
  },
  {
    phase: "shipped",
    month: "2026-04",
    order: 1,
    anchorId: "roadmap-overview",
    title: "Overview",
    eyebrow: "Release 1",
    summary:
      "We shipped Overview so leaders can scan the state of a team in one place before moving deeper into people, patterns, and follow-up actions.",
    details: [
      "Overview pulls the main signals together into a calmer first screen.",
      "The new surface reduces the need to jump between views just to understand the current team picture.",
    ],
    media: {
      type: "image",
      src: "/cooperly/roadmap-overview-v3.png",
      alt: "Overview-style team summary screen in Cooperly",
      caption: "A broader summary surface for team-level insight.",
    },
  },
  {
    phase: "shipped",
    month: "2026-04",
    order: 2,
    anchorId: "roadmap-slack-ms-teams",
    title: "Slack + Microsoft Teams integrations",
    eyebrow: "Release 2",
    summary:
      "We shipped Slack and Microsoft Teams integrations so reminders, summaries, and follow-up can land in the communication tools leaders already use instead of another tab.",
    details: [
      "Leaders can now bring Cooperly into the weekly communication flow without asking the team to live in another interface.",
      "This makes follow-up easier to notice and easier to act on inside the tools where work is already happening.",
      "Microsoft Teams has since been removed from Cooperly. Slack remains available.",
    ],
    links: [
      {
        href: "/integrations/slack",
        label: "Slack integration page",
      },
    ],
  },
  {
    phase: "shipped",
    month: "2026-05",
    order: 2,
    anchorId: "roadmap-ai-context-release",
    title: "MCP, AI clients, and collaboration integrations",
    eyebrow: "Release 2",
    summary:
      "We shipped the Cooperly MCP server, approved context access for Claude, ChatGPT, Linear Agent, and Notion Custom Agents, plus managed roadmap connections for Linear, Notion, ClickUp, and Asana.",
    details: [
      "Approved AI clients can request selected, permissioned, read-only Cooperly context for planning, writing, and preparation.",
      "Linear and Notion can also receive managed roadmap work. ClickUp and Asana receive managed roadmap work and team updates; their agent connections arrived in beta in August 2026.",
      "Cooperly remains the source of truth, and each destination keeps its own permissions and action boundaries.",
    ],
    links: [
      {
        href: "/integrations/mcp-server",
        label: "MCP server page",
      },
      {
        href: "/integrations/claude",
        label: "Claude integration page",
      },
      {
        href: "/integrations/chatgpt",
        label: "ChatGPT integration page",
      },
      {
        href: "/integrations/linear",
        label: "Linear page",
      },
      {
        href: "/integrations/notion",
        label: "Notion page",
      },
      {
        href: "/integrations/clickup",
        label: "ClickUp page",
      },
      {
        href: "/integrations/asana",
        label: "Asana page",
      },
    ],
  },
  {
    phase: "shipped",
    month: "2026-05",
    order: 1,
    title: "Memories and Facts for team members",
    eyebrow: "Release 1",
    summary:
      "Store durable context and notable facts for each team member so prompts, coaching, and follow-up become more specific over time.",
  },
  {
    phase: "shipped",
    month: "2026-06",
    order: 1,
    anchorId: "roadmap-organization-summary",
    title: "Organization Summary and Roadmap",
    eyebrow: "Release 1",
    summary:
      "We shipped Organization Summary and Roadmap so owners can see the combined picture across teams, compare it with company goals, and turn team-level signals into an account-level roadmap.",
    details: [
      "The organization overview rolls completed team overviews into one owner-only view with risk, issues, opportunities, strengths, roadmap, and source status.",
      "The roadmap can sync company-level action items into the configured delivery tools while Cooperly stays the source of truth.",
    ],
  },
  {
    phase: "shipped",
    month: "2026-06",
    order: 2,
    anchorId: "roadmap-random-coffee",
    title: "Random Coffee",
    eyebrow: "Release 2",
    summary:
      "We shipped Random Coffee to turn Cooperly's people context into low-pressure weekly conversations, pairing colleagues with a practical reason to meet and a small guide for where to start.",
    details: [
      "Cooperly plans a four-week cadence of lightweight 1:1 coffee cards, balancing lead-member check-ins with same-team and cross-team peer pairings while avoiding recent repeats.",
      "Each card gives both people the reason for the match, a simple conversation guide, and three work and three lifestyle prompts without exposing profile mechanics or assessment language.",
      "Personal notifications and reminders can reach people through Slack, Telegram, or e-mail, with team announcements in connected delivery tools when enabled.",
    ],
  },
  {
    phase: "shipped",
    month: "2026-07",
    anchorId: "roadmap-new-onboarding-experience",
    title: "New onboarding experience",
    eyebrow: "Activation",
    summary:
      "We shipped a calmer onboarding experience that helps owners set up the right context, choose the first useful move, and reach a realistic team workspace faster.",
    details: [
      "The new flow guides leaders through organization or team context, goals, and the first action without dropping them into an empty product surface.",
      "It makes the first setup path clearer for real teammates, candidate workflows, and demo workspaces, so Cooperly starts with useful context instead of guesswork.",
    ],
  },
  {
    phase: "shipped",
    month: "2026-08",
    anchorId: "roadmap-organization-teams-ai-context",
    title: "Organization, teams, and AI context",
    eyebrow: "Organization context",
    summary:
      "We expanded how Cooperly represents an organization, its teams, and the people in them, giving leaders more useful context in the workspace and in connected AI tools.",
    details: [
      "Organization administration and team roles are explicit. A person can work in one team and independently lead other teams, with access and notifications following those relationships.",
      "Administrators can assign existing people and review the consequences of team and role changes before confirming them.",
      "Facts and Memories now cover the team as well as its people. Leaders can date and pin working agreements, decisions, and context for the Team Coop Profile.",
      "Expanded MCP support brings approved, read-only context to Codex and developer clients, Claude Code, and Claude within the user's existing team permissions. ClickUp Brain and Super Agents and Asana AI Teammates can read the same context in beta.",
    ],
  },
  {
    phase: "shipped",
    month: "2026-09",
    anchorId: "roadmap-integration-expansion",
    title: "Integration expansion and team workflows",
    eyebrow: "Connected workflows",
    summary:
      "We expanded connected team workflows so leaders can review working context from Slack, people can complete their surveys in Telegram, and changes to team composition carry through the workspace.",
    details: [
      "Selected public Slack channels now provide daily suggestions for decisions, blockers, commitments, and explicitly stated work preferences. Leaders can confirm, correct, or reject facts in Slack or Cooperly, with source and reviewer attribution.",
      "The refreshed Telegram Mini App brings assigned Coop Profile, Fundamentals, and Pulse tasks into one place, with progress and completion states. Pulse results follow the correct survey period as membership changes.",
      "Team transfers preserve completed survey history and leadership in other teams, and create a people-change history. Administrators can manage survey participation separately from access to results.",
      "The in-product Help Center provides task-specific instructions, recurring leadership scenarios, and selected captioned walkthroughs in English and Russian, filtered by role and available features.",
    ],
  },
  {
    phase: "shipped",
    month: "2026-10",
    order: 1,
    anchorId: "roadmap-dark-stage",
    title: "Pulse, Fundamentals and Random Coffee on a dark stage",
    eyebrow: "Redesign",
    summary:
      "We redesigned the top of Pulse and Fundamentals for leaders and Random Coffee for everyone. Each section now opens on a dark stage with the main finding, the reason behind it and one chart or card.",
    details: [
      "Pulse shows each week as one bar of OK and not-OK answers. The full-screen chart shows the change across cycles, and comments open in their own window.",
      "Fundamentals name the weakest area by its average score and put two cycles side by side. Area scores stay visible until a second cycle shows a change.",
      "Random Coffee shows who you meet this week, why you were paired, and where to start.",
      "The anonymity rule did not change: a cycle with too few answers still shows no numbers.",
    ],
    media: {
      type: "previews",
      pillars: ["pulse", "fundamentals", "random-coffee"],
    },
  },
  {
    phase: "shipped",
    month: "2026-10",
    order: 2,
    anchorId: "roadmap-workspace-experience",
    title: "Workspace experience",
    eyebrow: "Workspace experience",
    summary:
      "We reorganized integration setup around the task people came to do and cut repeated loading across the workspace.",
    details: [
      "Roadmap delivery, notifications and assistant access each have their own setup and management path.",
      "Unfinished setup and onboarding keep their progress. Lists, filters and dialogs keep the current team and load only when needed.",
      "Next: shared ClickUp chats join Slack in the same daily fact review. Cooperly keeps the confirmed facts and their sources, not the messages.",
    ],
  },
  {
    phase: "shipped",
    month: "2026-11",
    order: 1,
    anchorId: "roadmap-facts",
    title: "Facts about people, confirmed by a person",
    eyebrow: "Facts",
    summary:
      "We shipped a new way for Cooperly to learn facts about people: from their own words, from one-on-one notes and documents, and from the Slack channels an admin picks. Cooperly uses a suggested fact only after a person confirms it.",
    details: [
      "People add facts in their own words. Leaders upload one-on-one notes or documents, and Cooperly suggests the facts it found. In the Slack channels an admin picks, Cooperly reads the day's messages overnight and suggests facts in the morning.",
      "Confirmed facts appear as margin notes on a person's profile, next to what their test said.",
      "The Team Overview rebuilds with that evidence and suggests a step and an owner. The step goes to Linear in one click; Cooperly checks it every day, and Monday's summary counts it once it is closed.",
      "From work chats Cooperly keeps facts, not messages, and stores them encrypted. Pulse stays anonymous.",
    ],
    media: {
      type: "video",
      embedUrl:
        "https://www.youtube-nocookie.com/embed/Pq1lho7nLoI?rel=0&modestbranding=1",
      title: "Cooperly autumn update: stop piecing your team together at 11 pm",
      caption: "The autumn update, told through one case: Leo's two jobs.",
    },
  },
  {
    phase: "planned",
    month: "2026-11",
    order: 2,
    anchorId: "roadmap-mcp-slack-actions",
    title: "Act on Cooperly from Claude, Codex and Slack",
    eyebrow: "MCP and Slack",
    summary:
      "People will be able to do from their AI assistant or from Slack what they can do with their data in Cooperly, with the same permissions.",
    details: [
      "The MCP server gets a shorter catalogue of task-based tools that can write as well as read: decide on suggested facts, add facts from a document or a conversation, and update recommendations and their linked tracker tasks.",
      "Every change shows a preview before it is saved and passes the same permission checks as the web app. Settings, access management and actions that cannot be undone stay in the web app.",
      "In Slack, fact reminders get confirm and reject buttons, App Home lists what is waiting for your decision, and Slackbot answers questions about the team through the Cooperly MCP server.",
      "ChatGPT keeps its current published app until a new version passes OpenAI's review.",
    ],
  },
  {
    phase: "planned",
    month: "2026-12",
    anchorId: "roadmap-agents-first",
    title: "Your team can run without you",
    eyebrow: "Agents-first interface",
    summary:
      "Cooperly is built for agents, not as another agent. It collects the knowledge nobody owns or keeps track of, together with what people say about their own work, and turns it into a detailed picture of the team, so people work and communicate better. You and your people reach that picture through the agents you already use: Claude, ChatGPT, Codex and Slackbot.",
    details: [
      "The sources are decisions, promises and blockers in Slack, ClickUp, Asana, Linear and Notion, and people's own answers in Coop Profile, Pulse and Fundamentals.",
      "The picture shows what nobody owns: decisions without an owner, promises nobody followed up, overdue and stuck tasks, and knowledge only one person holds. Each item links to the message or task it came from.",
      "Agents read the picture through MCP. Anyone on the team can ask who owns an area, what was decided or how a colleague prefers to work, and get an answer with its source instead of asking you.",
      "The first connection is free and needs no sign-up. In Slack, Cooperly reads only the public channels you select.",
    ],
    media: {
      type: "image",
      src: "/cooperly/roadmap/roadmap-agents-first-v2.png",
      width: 2172,
      height: 724,
      alt: "Cover: “Your team asks its AI. Not you.” Slack and Linear show billing slipped two weeks, the Cooperly team picture shows Serena owns the Acme date, and Priya agrees the new date with Serena from her Claude.",
    },
  },
];

const monthLabels: Record<MonthToken, string> = {
  "01": "Jan",
  "02": "Feb",
  "03": "Mar",
  "04": "Apr",
  "05": "May",
  "06": "Jun",
  "07": "Jul",
  "08": "Aug",
  "09": "Sep",
  "10": "Oct",
  "11": "Nov",
  "12": "Dec",
};

export function formatRoadmapMonth(month: RoadmapMonth) {
  const [year, monthToken] = month.split("-") as [string, MonthToken];
  return `${monthLabels[monthToken]} ${year}`;
}
